import { useMemo } from 'react';

import type { BlockFeature } from '~api/services';
import { getBlockColour } from '~features/blast-design-context/utils/colours';
import { Block, useSvgContext } from '~libs';
import type { Point } from '~types';
import { type ColourScale, coordsToSVGPoints } from '~utils';

/** Hardness levels in draw order: unknown at back so other block borders are visible */
const LAYER_HARDNESS_ORDER = ['UNKNOWN', 'AIR', 'SOFT', 'MEDIUM', 'HARD', 'EXTRAHARD'] as const;

type BlocksLayerProps = {
  /** Block features to render */
  blocks?: BlockFeature[];
  /** Currently selected block (for highlight) */
  selectedBlockId: string | null;
  /** Colour scale for block rendering */
  colourScale?: ColourScale;
  /** Callback when a block is clicked */
  onBlockClick: (block: BlockFeature) => void;
};

const BlocksLayer = ({ blocks, selectedBlockId, colourScale, onBlockClick }: BlocksLayerProps) => {
  const { transformer } = useSvgContext();

  /**
   * Sorts blocks by hardness layer order so UNKNOWN blocks render at the back
   * and EXTRAHARD blocks render on top, making borders visible.
   */
  const sortedBlocks = useMemo(() => {
    if (!blocks) return [];
    return [...blocks].sort(
      (a, b) =>
        LAYER_HARDNESS_ORDER.indexOf(a.properties.hardness as (typeof LAYER_HARDNESS_ORDER)[number]) -
        LAYER_HARDNESS_ORDER.indexOf(b.properties.hardness as (typeof LAYER_HARDNESS_ORDER)[number]),
    );
  }, [blocks]);

  /**
   * Pre-computes colour and SVG points for each sorted block.
   * This will avoid doing these calculations every render for every block in the render body.
   */
  const blockRenderData = useMemo(() => {
    if (!transformer || !colourScale) return [];
    return sortedBlocks.map((block, index) => {
      const colour = getBlockColour(block, colourScale);
      const points = coordsToSVGPoints(block.geometry.coordinates[0] as Point[], transformer);
      return { block, colour, points, index };
    });
  }, [sortedBlocks, colourScale, transformer]);

  return (
    <g className="blocks">
      {blockRenderData.map(({ block, colour, points, index }) => {
        const blockId = `${block.properties.xcentre}-${block.properties.ycentre}-${block.properties.zcentre}`;
        return (
          <Block
            className="group-focus-visible:stroke-4 group-[&.block--selected]:stroke-4"
            key={`block-${blockId}`}
            points={points}
            fill={colour.fill}
            stroke={colour.stroke}
            strokeWidth={2}
            opacity={colour.opacity}
            isSelected={selectedBlockId === blockId}
            index={index}
            title={`${block.properties.hardness} block`}
            onClick={() => onBlockClick(block)}
          />
        );
      })}
    </g>
  );
};

export { BlocksLayer };
